import { Input } from '@/components/ui/input';
import { useTravelEsimLineSearch } from '@/hooks/useTravelEsim';
import { findFleetDuplicate, formatLineHolder } from '@/lib/travel-esim';
import { useDebouncedValue } from '@mantine/hooks';
import type { MobileLine } from '@octave/contracts';
import { AlertTriangle, Loader2, Search } from 'lucide-react';
import { useState } from 'react';

/** Fleet search of the order modal: name, e-mail or number over the whole organization. */
export function LineSearch({
  organizationId,
  onSelect,
}: {
  organizationId: string | undefined;
  onSelect: (line: MobileLine) => void;
}) {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const { data: lines, isFetching } = useTravelEsimLineSearch(
    organizationId,
    debounced,
  );
  const showResults = debounced.trim().length >= 2;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Rechercher par nom, e-mail ou numéro"
          className="h-11 rounded-xl pl-9"
          autoFocus
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
        )}
      </div>
      {showResults && (
        <ul className="max-h-56 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
          {lines && lines.length > 0 ? (
            lines.map((line) => (
              <li key={line.id}>
                <button
                  type="button"
                  onClick={() => onSelect(line)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50 cursor-pointer"
                >
                  <span>
                    <span className="font-medium text-slate-900">
                      {formatLineHolder(line)}
                    </span>
                    <span className="text-slate-500"> · {line.number}</span>
                  </span>
                  {line.phonePlan && (
                    <span className="text-xs text-slate-400 shrink-0">
                      {line.phonePlan.name}
                    </span>
                  )}
                </button>
              </li>
            ))
          ) : (
            <li className="px-4 py-3 text-sm text-slate-500">
              {isFetching ? 'Recherche...' : 'Aucune ligne ne correspond.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export function DuplicateAlert({
  organizationId,
  email,
  onAttach,
}: {
  organizationId: string | undefined;
  email: string;
  onAttach: (line: MobileLine) => void;
}) {
  const [debounced] = useDebouncedValue(email, 400);
  const looksLikeEmail = /\S+@\S+\.\S+/.test(debounced.trim());
  const { data: lines } = useTravelEsimLineSearch(
    organizationId,
    looksLikeEmail ? debounced : '',
  );
  const duplicate = looksLikeEmail
    ? findFleetDuplicate(lines ?? [], debounced)
    : null;

  if (!duplicate) return null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        Attention, cet e-mail existe déjà dans la flotte (
        {formatLineHolder(duplicate)}). Vous pouvez poursuivre hors flotte, ou{' '}
        <button
          type="button"
          onClick={() => onAttach(duplicate)}
          className="font-medium underline cursor-pointer"
        >
          rattacher l'eSIM à sa ligne Eiko
        </button>
        .
      </p>
    </div>
  );
}
